import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useMutation } from "@tanstack/react-query";
import { CheckCircle2, FileUp, Loader2, Upload } from "lucide-react";

import { extractDxf, extractIfc, isBackendProjectId, saveLayerOverrides } from "@/api/client";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  useExtractionStore,
  type ExtractedField,
} from "./useExtractionStore";
import { DxfVisualPreview } from "./DxfVisualPreview";
import { EMPTY_IFC, extractIfcDepth, type IfcDepth } from "./extractionMapper";
import { LayerMappingReview, type LayerMappingReviewData } from "./LayerMappingReview";

import type { SourceKind } from "./useExtractionStore";

interface DropzoneProps {
  onPrefill: (
    values: Record<string, unknown>,
    ifcDepth: IfcDepth,
    sourceKind: SourceKind,
  ) => void;
  projectId?: string;
  /** When provided, the dropzone also accepts PDFs and hands them to the
   * caller for document/situacija routing — one basket, the system routes
   * (same promise the landing makes). */
  onPdfFiles?: (files: File[]) => void;
  /** Slim top-of-page strip (F1 konzola layout) instead of the tall hero
   * card — same behaviour, a third of the height. */
  compact?: boolean;
}

/**
 * The one basket on Step 2. Handles DXF and IFC itself (extraction →
 * form prefill); with ``onPdfFiles`` it also accepts PDFs and delegates
 * them to the recognize/situacija router, so the user never has to know
 * which extractor a file belongs to.
 */
export function Dropzone({ onPrefill, projectId, onPdfFiles, compact }: DropzoneProps) {
  const [filename, setFilename] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [rejectedMsg, setRejectedMsg] = useState<string | null>(null);
  const [lastFiles, setLastFiles] = useState<File[] | null>(null);
  const [layerReview, setLayerReview] = useState<LayerMappingReviewData | null>(null);
  const [geometryPreview, setGeometryPreview] = useState<Record<string, unknown> | null>(null);
  const setExtraction = useExtractionStore((s) => s.setExtraction);

  const mutation = useMutation({
    mutationFn: async (files: File[]) => {
      const file = files[0];
      const lower = file.name.toLowerCase();
      const isIfc = lower.endsWith(".ifc");
      setFilename(file.name);
      const raw = (await (isIfc ? extractIfc(file) : extractDxf(files, projectId))) as Record<
        string,
        unknown
      >;
      return { raw, isIfc };
    },
    onSuccess: ({ raw, isIfc }) => {
      const extracted = (raw.extracted as Record<string, ExtractedField>) ?? {};
      const warnings = (raw.warnings as string[]) ?? [];
      const sourceKind: SourceKind = isIfc ? "ifc" : "dxf";
      setExtraction(extracted, warnings, sourceKind);
      const review = isIfc ? null : buildLayerReview(raw);
      const autoOverrides =
        !isIfc && projectId && Object.keys(extracted).length === 0
          ? highConfidencePlanningOverrides(review)
          : null;
      if (autoOverrides) {
        remapMutation.mutate(autoOverrides);
        return;
      }

      const values: Record<string, unknown> = {};
      for (const [key, payload] of Object.entries(extracted)) {
        if (payload && payload.value != null && payload.value !== "") {
          values[key] = payload.value;
        }
      }

      const ifcDepth: IfcDepth = isIfc ? extractIfcDepth(raw) : EMPTY_IFC;

      onPrefill(values, ifcDepth, sourceKind);
      setLayerReview(review);
      setGeometryPreview(isIfc ? null : getGeometryPreview(raw));
      setDone(true);
    },
  });

  const remapMutation = useMutation({
    mutationFn: async (overrides: Record<string, string>) => {
      if (!projectId) throw new Error("Projekt mora biti spremljen prije mapiranja layera.");
      if (!lastFiles?.length) throw new Error("Nedostaje DXF datoteka za ponovnu analizu.");
      const cleaned = Object.fromEntries(Object.entries(overrides).filter(([, layer]) => layer));
      if (isBackendProjectId(projectId)) {
        await saveLayerOverrides(projectId, cleaned);
        return (await extractDxf(lastFiles, projectId)) as Record<string, unknown>;
      }
      return (await extractDxf(lastFiles, undefined, cleaned)) as Record<string, unknown>;
    },
    onSuccess: (raw) => {
      const extracted = (raw.extracted as Record<string, ExtractedField>) ?? {};
      const warnings = (raw.warnings as string[]) ?? [];
      setExtraction(extracted, warnings, "dxf");
      const values: Record<string, unknown> = {};
      for (const [key, payload] of Object.entries(extracted)) {
        if (payload && payload.value != null && payload.value !== "") {
          values[key] = payload.value;
        }
      }
      onPrefill(values, EMPTY_IFC, "dxf");
      setLayerReview(null);
      setGeometryPreview(getGeometryPreview(raw));
      setDone(true);
    },
  });

  const onDrop = useCallback(
    (accepted: File[]) => {
      if (!accepted.length) return;
      setRejectedMsg(null);
      const pdfs = accepted.filter((f) => f.name.toLowerCase().endsWith(".pdf"));
      const cad = accepted.filter((f) => !f.name.toLowerCase().endsWith(".pdf"));
      if (pdfs.length) onPdfFiles?.(pdfs);
      if (!cad.length) return;
      setDone(false);
      setLastFiles(cad);
      setGeometryPreview(null);
      mutation.mutate(cad);
    },
    [mutation, onPdfFiles],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    // A silently filtered file (most often .dwg) looks like the app "ate"
    // it — say what happened and what to do instead.
    onDropRejected: (rejections) => {
      const names = rejections.map((r) => r.file.name);
      const dwg = names.filter((n) => n.toLowerCase().endsWith(".dwg"));
      setRejectedMsg(
        dwg.length
          ? `${dwg.join(", ")}: DWG format nije podržan — izvezi nacrt kao DXF (u AutoCAD-u: SAVEAS → DXF) ili IFC.`
          : `${names.join(", ")}: format nije podržan.`,
      );
    },
    multiple: true,
    accept: {
      "application/dxf": [".dxf"],
      // .dwg intentionally NOT accepted for the pilot: the ODA File
      // Converter isn't installed in the production image, so advertising
      // DWG would only sell an error. Re-add here (and in LandingPage)
      // when ODA ships in the Dockerfile.
      "application/x-step": [".ifc"],
      ...(onPdfFiles ? { "application/pdf": [".pdf"] } : {}),
    },
  });
  const isProcessing = mutation.isPending || remapMutation.isPending;

  const title = mutation.isPending
    ? `Obrađujem ${filename ?? "datoteku"}…`
    : remapMutation.isPending
      ? `Primjenjujem layer mapping za ${filename ?? "datoteku"}…`
      : done
        ? `Pre-popunjeno iz ${filename}`
        : isDragActive
          ? "Pusti datoteku za obradu"
          : onPdfFiles
            ? "Povuci .ifc / .dxf / .pdf ovdje — ili klikni"
            : "Povuci .ifc / .dxf ovdje";
  const hint = onPdfFiles
    ? "Sustav sam rasporedi: nacrt puni polja, PDF-ovi (situacija, sudski registar, HKA, posebni uvjeti…) sjedaju na svoje retke niže."
    : "Polja niže se automatski popune i označe pouzdanošću — zelena su pouzdana, žuta provjeri prije generiranja.";
  const icon = isProcessing ? (
    <Loader2 className={compact ? "h-4 w-4 animate-spin" : "h-6 w-6 animate-spin"} />
  ) : done ? (
    <CheckCircle2 className={compact ? "h-4 w-4" : "h-6 w-6"} />
  ) : (
    <FileUp className={compact ? "h-4 w-4" : "h-6 w-6"} />
  );

  return (
    <div
      {...getRootProps()}
      className={cn(
        "cursor-pointer rounded-xl border-2 border-dashed bg-white shadow-sm transition-all",
        compact ? "p-3" : "flex flex-col items-center justify-center p-10 text-center",
        isDragActive
          ? "border-ind-500 bg-ind-50"
          : "border-stone-300 hover:border-ind-400 hover:bg-stone-50",
        isProcessing && "pointer-events-none opacity-70",
      )}
    >
      <input {...getInputProps()} />
      {compact ? (
        // Slim strip: icon · title · hint · button in one row.
        <div className="flex items-center gap-3">
          <div
            className={cn(
              "flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full",
              done ? "bg-green-100 text-green-700" : "bg-ind-100 text-ind-700",
            )}
          >
            {icon}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-[0.9rem] font-bold leading-tight text-stone-800">
              {title}
            </h3>
            <p className="truncate text-[0.76rem] text-stone-500" title={hint}>
              {hint}
            </p>
          </div>
          {!isProcessing && (
            <Button variant="outline" size="sm" type="button" className="flex-shrink-0">
              <Upload className="h-3.5 w-3.5" />
              Odaberi
            </Button>
          )}
        </div>
      ) : (
        <>
          <div
            className={cn(
              "mb-3 flex h-14 w-14 items-center justify-center rounded-full",
              done ? "bg-green-100 text-green-700" : "bg-ind-100 text-ind-700",
            )}
          >
            {icon}
          </div>
          <h3 className="text-[1.05rem] font-bold text-stone-800">{title}</h3>
          <p className="mt-1 max-w-md text-[0.85rem] text-stone-500">{hint}</p>
          {!isProcessing && (
            <Button variant="outline" size="sm" className="mt-4" type="button">
              <Upload className="h-3.5 w-3.5" />
              Odaberi datoteku
            </Button>
          )}
        </>
      )}
      {rejectedMsg && (
        <div className="mt-4 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-[0.8rem] text-amber-800">
          {rejectedMsg}
        </div>
      )}
      {mutation.error instanceof Error && (
        <div className="mt-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-[0.8rem] text-red-700">
          Greška: {mutation.error.message}
        </div>
      )}
      {remapMutation.error instanceof Error && (
        <div className="mt-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-[0.8rem] text-red-700">
          Greška mapiranja: {remapMutation.error.message}
        </div>
      )}
      {layerReview && (
        <LayerMappingReview
          data={layerReview}
          saving={remapMutation.isPending}
          onConfirm={(overrides) => remapMutation.mutate(overrides)}
        />
      )}
      {geometryPreview && <DxfVisualPreview geometry={geometryPreview} />}
    </div>
  );
}

function getGeometryPreview(raw: Record<string, unknown>): Record<string, unknown> | null {
  return raw.geometry_preview && typeof raw.geometry_preview === "object"
    ? (raw.geometry_preview as Record<string, unknown>)
    : null;
}

function buildLayerReview(raw: Record<string, unknown>): LayerMappingReviewData | null {
  const files = Array.isArray(raw.files) ? raw.files : [];
  const first = files[0] as Record<string, unknown> | undefined;
  const calibration = first?.calibration as Record<string, unknown> | undefined;
  const roles = calibration?.roles as LayerMappingReviewData["roles"] | undefined;
  if (!first || !roles) return null;
  const classification = String(first.classification ?? "unknown");
  const confidence = Number(first.classification_confidence ?? 0);
  const needsReview =
    classification === "unknown" ||
    confidence < 0.65 ||
    Object.values(roles).some((role) => role && role.confidence > 0 && role.confidence < 0.7);
  if (!needsReview) return null;
  return {
    fileName: String(first.file_name ?? "DXF"),
    classification,
    classificationConfidence: confidence,
    roles,
  };
}

function highConfidencePlanningOverrides(
  review: LayerMappingReviewData | null,
): Record<string, string> | null {
  if (!review) return null;
  const roles = ["plot", "kig_footprint", "regulation_line"] as const;
  const overrides: Record<string, string> = {};
  for (const role of roles) {
    const match = review.roles[role];
    if (match?.layer && match.confidence >= 0.85) overrides[role] = match.layer;
  }
  return overrides.plot && overrides.kig_footprint ? overrides : null;
}
